import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { ScrollText, Check, ShieldAlert } from "lucide-react";

const STATUSES = [
  { id: "active", label: "Active", tint: "bg-emerald-500 text-[#04140d]" },
  { id: "suspended", label: "Suspended", tint: "bg-amber-500 text-[#0B0F17]" },
  { id: "ended", label: "Ended", tint: "bg-red-500 text-white" },
];

export const ReferralScheme = () => {
  const [scheme, setScheme] = useState(null);
  const [rate, setRate] = useState(10);
  const [terms, setTerms] = useState("");

  const load = async () => {
    const s = await api.referralScheme();
    setScheme(s); setRate(s.commission_gbp); setTerms(s.terms || "");
  };
  useEffect(() => { load(); }, []);

  const setStatus = async (status) => {
    const s = await api.updateReferralScheme({ status });
    setScheme(s);
    toast.success(`Referral scheme ${status}.`);
  };
  const saveRate = async () => { const s = await api.updateReferralScheme({ commission_gbp: Number(rate) }); setScheme(s); toast.success("Commission updated."); };
  const saveTerms = async () => { const s = await api.updateReferralScheme({ terms }); setScheme(s); toast.success("Terms saved."); };

  if (!scheme) return null;

  return (
    <div data-testid="referral-scheme" className="rounded-2xl border border-white/10 bg-[#0F1420] p-4">
      <h3 className="kz-head font-bold mb-1 flex items-center gap-2"><ScrollText size={16} className="text-emerald-400" /> Referral scheme & terms</h3>
      <p className="text-xs text-slate-500 mb-4">Your legal right to amend, suspend or end the programme — built in. Changing this affects commission going forward only.</p>

      <div className="mb-4">
        <div className="text-xs text-slate-400 mb-2">Scheme status</div>
        <div className="flex gap-1.5">
          {STATUSES.map((st) => (
            <button key={st.id} data-testid={`scheme-status-${st.id}`} onClick={() => setStatus(st.id)}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${scheme.status === st.id ? st.tint : "bg-white/[0.06] text-slate-300 hover:bg-white/[0.1]"}`}>
              {scheme.status === st.id && <Check size={12} className="inline mr-1 -mt-0.5" />}{st.label}
            </button>
          ))}
        </div>
        {scheme.status !== "active" && (
          <p className="text-[11px] text-amber-400 mt-2 flex items-center gap-1"><ShieldAlert size={12} /> Scheme {scheme.status} — no new commission is accruing (already-earned is honoured).</p>
        )}
      </div>

      <div className="mb-4">
        <div className="text-xs text-slate-400 mb-2">Commission per active member (£/month)</div>
        <div className="flex items-center gap-2">
          <input data-testid="scheme-rate-input" type="number" min="0" value={rate} onChange={(e) => setRate(e.target.value)}
            className="w-24 bg-[#0B0F17] border border-white/10 rounded-lg px-2.5 py-1.5 text-sm outline-none focus:border-emerald-500/40" />
          <button data-testid="scheme-rate-save" onClick={saveRate} className="text-[11px] font-semibold px-3 py-1.5 rounded-lg bg-emerald-500 text-[#04140d] hover:bg-emerald-400 transition-all">Save rate</button>
        </div>
      </div>

      <div>
        <div className="text-xs text-slate-400 mb-2">Programme terms (small print)</div>
        <textarea data-testid="scheme-terms" value={terms} onChange={(e) => setTerms(e.target.value)} rows={8}
          className="w-full bg-[#0B0F17] border border-white/10 rounded-lg px-2.5 py-2 text-xs text-slate-300 outline-none focus:border-emerald-500/40 resize-y leading-relaxed" />
        <button data-testid="scheme-terms-save" onClick={saveTerms} className="mt-2 text-[11px] font-semibold px-3 py-1.5 rounded-lg bg-white/[0.06] text-slate-200 hover:bg-white/[0.1] transition-all">Save terms</button>
        <p className="text-[10px] text-slate-600 mt-2">Replace with your solicitor-drafted terms. Keep the variation/termination clause so amending or ending the scheme stays lawful.</p>
      </div>
    </div>
  );
};
